import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, Menu, X } from 'lucide-react';
import { isAuthenticated, clearAuth, getUser } from '../utils/auth';

function Navbar() {
  const navigate = useNavigate();
  const isAuth = isAuthenticated();
  const user = getUser();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    clearAuth();
    setIsMobileMenuOpen(false);
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 border-b py-3 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm border-slate-200' : 'bg-white border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 group">
          <ShieldCheck className="w-10 h-10 text-slate-900 group-hover:text-slate-700 transition-colors" strokeWidth={2} />
          <span className="text-2xl font-bold tracking-tight text-slate-900 group-hover:text-slate-700 transition-colors">
            InternStacks
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8 text-sm font-medium text-slate-600">
          <Link to="/" className="hover:text-slate-900 transition-colors">
            Home
          </Link>
          <Link to="/programs" className="hover:text-slate-900 transition-colors">
            Programs
          </Link>
          {isAuth && (
            <>
              <Link to="/dashboard" className="hover:text-slate-900 transition-colors">
                Dashboard
              </Link>
              {user && (
                <Link to={`/profile/${user._id}`} className="hover:text-slate-900 transition-colors">
                  Profile
                </Link>
              )}
            </>
          )}
        </nav>

        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex items-center space-x-4">
          {isAuth ? (
            <button 
              onClick={handleLogout}
              className="px-5 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors rounded-sm"
            >
              Logout
            </button>
          ) : (
            <>
              <Link 
                to="/login" 
                className="px-5 py-2 text-sm font-semibold text-slate-900 hover:text-slate-700 transition-colors"
              >
                Login
              </Link>
              <Link 
                to="/register" 
                className="px-5 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors rounded-sm"
              >
                Register
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 text-slate-900 hover:bg-slate-100 rounded-sm transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <nav className="flex flex-col px-6 py-4 space-y-4">
            <Link 
              to="/" 
              onClick={closeMobileMenu}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors py-2"
            >
              Home
            </Link>
            <Link 
              to="/programs" 
              onClick={closeMobileMenu}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors py-2"
            >
              Programs
            </Link>
            
            {isAuth ? (
              <>
                <Link 
                  to="/dashboard" 
                  onClick={closeMobileMenu}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors py-2"
                >
                  Dashboard
                </Link>
                {user && (
                  <Link 
                    to={`/profile/${user._id}`} 
                    onClick={closeMobileMenu}
                    className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors py-2"
                  >
                    Profile
                  </Link>
                )}
                <button 
                  onClick={handleLogout}
                  className="text-left px-5 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors rounded-sm"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  onClick={closeMobileMenu}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors py-2"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  onClick={closeMobileMenu}
                  className="px-5 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors rounded-sm text-center"
                >
                  Register
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

export default Navbar;
